import { useCallback, useId, useState, type FormEvent } from 'react';
import { Button, Dialog, Field, Input, useToast } from '@/components/ui';
import { isApiError, MAX_CONVERSATION_TITLE, useUpdateConversation, type Conversation } from '@/lib/api';

export interface RenameConversationDialogProps {
  conversation: Conversation;
  onClose: () => void;
}

/** A regra da API para o título, já aparado: de 1 a {@link MAX_CONVERSATION_TITLE} caracteres. */
function titleProblem(title: string): string | null {
  if (title.length === 0) return 'Dê um nome ao chat.';
  if (title.length > MAX_CONVERSATION_TITLE) return `Use até ${MAX_CONVERSATION_TITLE} caracteres.`;
  return null;
}

/**
 * "Editar nome do chat": o nome atual vem selecionado, para ser trocado de uma
 * vez, e o Enter salva. Um nome igual ao de antes fecha sem gravar; a recusa do
 * servidor aparece no próprio campo.
 */
export function RenameConversationDialog({ conversation, onClose }: RenameConversationDialogProps): JSX.Element {
  const update = useUpdateConversation();
  const { toast } = useToast();
  const formId = useId();
  const [title, setTitle] = useState(conversation.title);
  const [error, setError] = useState<string | null>(null);
  // Estável: um ref novo a cada render selecionaria o texto de novo a cada tecla.
  const selectOnMount = useCallback((input: HTMLInputElement | null) => input?.select(), []);

  const save = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (update.isPending) return;
    const next = title.trim();
    const problem = titleProblem(next);
    if (problem) {
      setError(problem);
      return;
    }
    if (next === conversation.title) {
      onClose();
      return;
    }
    setError(null);
    try {
      await update.mutateAsync({ id: conversation.id, patch: { title: next } });
      toast({ tone: 'success', title: 'Nome do chat atualizado.' });
      onClose();
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Não foi possível salvar o nome. Tente novamente.');
    }
  };

  return (
    <Dialog
      open
      onClose={() => !update.isPending && onClose()}
      size="sm"
      title="Editar nome do chat"
      dismissible={!update.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={update.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form={formId} variant="primary" loading={update.isPending}>
            Salvar
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={(event) => void save(event)} noValidate>
        <Field label="Nome do chat" hideLabel error={error ?? undefined}>
          {(controlProps) => (
            <Input
              {...controlProps}
              ref={selectOnMount}
              value={title}
              maxLength={MAX_CONVERSATION_TITLE}
              autoFocus
              onChange={(event) => setTitle(event.target.value)}
            />
          )}
        </Field>
      </form>
    </Dialog>
  );
}
